import { g, layers, LayerClass } from './globals';
import { EventBus, VectorSelection } from '@hcie/shared';
// We use events to notify the UI instead of direct imports to break circular dependencies.
import { DocumentState } from './types';

declare global {
    interface Window {
        applyZoom(zoom: number): void;
        openNewImageDialog(): void;
        vectorSelection: VectorSelection | null;
        dispatchEvent(event: Event): boolean;
    }
}

export class ImageDocument implements DocumentState {
    id: string;
    name: string;
    width: number;
    height: number;
    layers: LayerClass[];
    activeLayerIndex: number;
    undoStack: any[];
    undoIndex: number;
    zoom: number;
    scrollLeft: number;
    scrollTop: number;
    modified: boolean;
    filepath: string;
    selectionActive: boolean = false;
    selectionMask: any = null;
    selectionBorder: any[] = [];
    selectionCanvas: HTMLCanvasElement | null = null;
    vectorSelection: VectorSelection | null = null;

    constructor(name = "Untitled", width = 500, height = 500) {
        this.id = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        this.name = name;
        this.filepath = '';
        this.width = width;
        this.height = height;
        this.layers = [new LayerClass("Background", width, height)];
        this.activeLayerIndex = 0;
        this.undoStack = [];
        this.undoIndex = -1;
        this.zoom = 1.0;
        this.scrollLeft = 0;
        this.scrollTop = 0;
        this.modified = false;
    }
}

// Document management
if (!g.documents) (g as any).documents = [];
if (g.activeDocumentIndex === undefined) (g as any).activeDocumentIndex = -1;

/**
 * Create the initial document from the existing state
 */
export function initDocuments() {
    if (g.documents.length === 0) {
        const doc = new ImageDocument("Image 1", g.image_width, g.image_height);
        // Transfer existing layers into the first document
        doc.layers = [...layers];
        doc.activeLayerIndex = g.activeLayerIndex;
        doc.zoom = g.zoom;
        doc.filepath = g.filepath;
        g.documents.push(doc);
        (g as any).activeDocumentIndex = 0;
    }
    switchDocument(0);
}

/**
 * Create a new blank document
 */
export function newDocument(name?: string, width?: number, height?: number) {
    const docName = name || `Image ${g.documents.length + 1}`;
    const w = width || g.image_width;
    const h = height || g.image_height;
    const doc = new ImageDocument(docName, w, h);
    g.documents.push(doc);
    switchDocument(g.documents.length - 1);
}

/**
 * Close a document by index
 */
/**
 * Close a document by index
 */
export function closeDocument(index: number) {
    if (index < 0 || index >= g.documents.length) return;
    
    g.documents.splice(index, 1);
    
    if (g.documents.length === 0) {
        return closeAllDocuments();
    }

    // Adjust active index if it was at or after the closed index
    if (g.activeDocumentIndex >= g.documents.length) {
        (g as any).activeDocumentIndex = g.documents.length - 1;
    }
    
    // Switch to the remaining document
    switchDocument(g.activeDocumentIndex);
}

/**
 * Close all active documents
 */
export function closeAllDocuments() {
    g.documents.length = 0;
    (g as any).activeDocumentIndex = -1;
    layers.length = 0;
    g.image_width = 800; // Reset to safe defaults
    g.image_height = 600;
    
    // Notify UI that no docs left
    notifyTabsChanged();
    EventBus.emit('RENDER_LAYERS');
}

/**
 * Save current state to the active document before switching
 */
export function saveCurrentDocumentState() {
    const doc = g.documents[g.activeDocumentIndex] as ImageDocument;
    if (!doc) return;

    doc.layers = [...layers];
    doc.activeLayerIndex = g.activeLayerIndex;
    doc.zoom = g.zoom;
    doc.filepath = g.filepath;

    const container = document.getElementById('canvasScrollArea');
    if (container) {
        doc.scrollLeft = container.scrollLeft;
        doc.scrollTop = container.scrollTop;
    }

    // AI_GUARDRAIL: Document State Isolation
    // Selection state (active, mask, border) MUST be saved per document.
    doc.selectionActive = g.isSelectionActive;
    doc.selectionMask = g.selectionMask;
    doc.selectionBorder = g.selectionBorder;
    doc.selectionCanvas = g.selectionCanvas as HTMLCanvasElement;
    doc.vectorSelection = (window as any).vectorSelection || null;
}

/**
 * Switch to a different document
 */
export function switchDocument(index: number) {
    if (index < 0 || index >= g.documents.length) return;
    if (g.activeDocumentIndex >= 0 && g.activeDocumentIndex < g.documents.length) {
        saveCurrentDocumentState();
    }

    (g as any).activeDocumentIndex = index;
    const doc = g.documents[index] as ImageDocument;

    // Restore document state to globals
    layers.length = 0;
    doc.layers.forEach(l => layers.push(l));
    
    g.activeLayerIndex = doc.activeLayerIndex;
    g.zoom = doc.zoom;
    g.image_width = doc.width;
    g.image_height = doc.height;
    g.filepath = doc.filepath || '';

    // Resize canvases to match the new document
    if (typeof window !== 'undefined') {
        const can = document.getElementById('drawingCanvas') as HTMLCanvasElement;
        if (can) {
            can.width = doc.width;
            can.height = doc.height;
        }
        // Use local property if available globally
        const tempCan = (window as any).tempCanvas;
        if (tempCan) {
            tempCan.width = doc.width;
            tempCan.height = doc.height;
        }
        const origCan = (window as any).originalCanvas;
        if (origCan) {
            origCan.width = doc.width;
            origCan.height = doc.height;
        }
    }

    // Apply zoom
    if (typeof window.applyZoom === 'function') {
        window.applyZoom(doc.zoom);
    } else {
        EventBus.emit('APPLY_ZOOM', { zoom: doc.zoom });
    }

    // Restore scroll position
    const container = document.getElementById('canvasScrollArea');
    if (container) {
        setTimeout(() => {
            container.scrollLeft = doc.scrollLeft;
            container.scrollTop = doc.scrollTop;
        }, 10);
    }

    // Restore Selection State
    g.isSelectionActive = doc.selectionActive || false;
    g.selectionMask = doc.selectionMask || null;
    g.selectionBorder = doc.selectionBorder || [];
    g.selectionCanvas = doc.selectionCanvas || null;
    (window as any).vectorSelection = doc.vectorSelection || null;

    // Trigger properties panel update if needed
    EventBus.emit('VECTOR_SHAPE_SELECTION_CHANGED', null);

    // Re-render layers and UI via Event Bus
    EventBus.emit('DOCUMENT_SWITCHED', { index });

    notifyTabsChanged();
    console.log(`Switched to document "${doc.name}"`);
}

/**
 * Notifies the UI that document tabs need re-rendering.
 */
export function notifyTabsChanged() {
    const tabData = g.documents.map((doc: any, i: number) => ({
        name: doc.name,
        index: i,
        active: i === g.activeDocumentIndex,
        modified: doc.modified
    }));
    EventBus.emit('DOCUMENT_TABS_CHANGED', { tabs: tabData });
}

// No more window bindings in core document.ts.
